/**
 * Correção de valores do roteiro fiscal pelo time Tax: clicar num CFOP, ICMS,
 * PIS/COFINS, IPI ou CST abre um formulário ancorado no valor; corrigido, a
 * célula mostra o valor novo com o calculado riscado abaixo.
 */

import { AlertCircle, Check, Loader2, Pencil } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TableCell } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { dataBR } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { CampoSubstituivel, CorrecaoTax, NotaFiscal, SubstituicoesNfs } from "@/types";

/** Colunas CFOP…CST do IPI, na ordem da tabela — as que o Tax pode corrigir. */
export const CAMPOS_FISCAIS: { campo: CampoSubstituivel; rotulo: string; className?: string }[] = [
  { campo: "cfop", rotulo: "CFOP", className: "font-extrabold" },
  { campo: "icms", rotulo: "ICMS", className: "font-extrabold" },
  { campo: "cstIcms", rotulo: "CST do ICMS" },
  { campo: "pis", rotulo: "PIS/COFINS", className: "whitespace-nowrap" },
  { campo: "cstPis", rotulo: "CST do PIS/COFINS" },
  { campo: "ipi", rotulo: "IPI" },
  { campo: "cstIpi", rotulo: "CST do IPI" },
];

export const ROTULO_CAMPO = Object.fromEntries(
  CAMPOS_FISCAIS.map(({ campo, rotulo }) => [campo, rotulo]),
) as Record<CampoSubstituivel, string>;

/** Estado da última gravação das correções, para o indicador do cabeçalho. */
export type EstadoGravacao = "salvando" | "salvo" | "erro" | null;

/** Correção nova (valor e motivo) ou `null` para voltar ao valor calculado. */
export type Corrigir = (
  chave: string,
  campo: CampoSubstituivel,
  correcao: Pick<CorrecaoTax, "valor" | "motivo"> | null,
) => void;

/** Correções em lista, na ordem das colunas, para contar e resumir. */
export function listarCorrecoes(subs: SubstituicoesNfs | undefined) {
  return Object.entries(subs ?? {}).flatMap(([chave, campos]) =>
    CAMPOS_FISCAIS.flatMap(({ campo, rotulo }) => {
      const correcao = campos?.[campo];
      return correcao ? [{ chave, campo, rotulo, correcao }] : [];
    }),
  );
}

/** "Calculado: 5.102 → Tax: 5.905 · por Ana em 26/09/2026 · Remessa para armazém". */
function descrever(c: CorrecaoTax, calculado: string | null) {
  return [
    `Calculado: ${c.original ?? calculado ?? "—"} → Tax: ${c.valor}`,
    c.por && `por ${c.por}${c.em ? ` em ${dataBR(c.em)}` : ""}`,
    c.motivo,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function FormCorrecao({
  rotulo,
  correcao,
  calculado,
  multilinha = false,
  onAplicar,
  onRestaurar,
  onCancelar,
}: {
  rotulo: string;
  correcao: CorrecaoTax | undefined;
  /** Valor do motor, que já vem no campo de quem corrige um texto longo (multilinha). */
  calculado?: string | null;
  /** Texto longo, em várias linhas, em vez de um valor curto. */
  multilinha?: boolean;
  onAplicar: (valor: string, motivo: string) => void;
  onRestaurar: () => void;
  onCancelar: () => void;
}) {
  const [valor, setValor] = React.useState(correcao?.valor ?? (multilinha ? (calculado ?? "") : ""));
  const [motivo, setMotivo] = React.useState(correcao?.motivo ?? "");
  const id = React.useId();
  const base = correcao?.valor ?? (multilinha ? (calculado ?? "").trim() : "");
  const mudou = valor.trim() !== base || motivo.trim() !== (correcao?.motivo ?? "");
  // Sem valor não há correção: para voltar ao calculado existe "Restaurar".
  const podeAplicar = Boolean(valor.trim()) && mudou;

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (podeAplicar) onAplicar(valor.trim(), motivo.trim());
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-valor`}>{rotulo} correto</Label>
        {multilinha ? (
          <Textarea
            id={`${id}-valor`}
            autoFocus
            rows={6}
            maxLength={2000}
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            className="min-h-0 text-xs"
          />
        ) : (
          <Input
            id={`${id}-valor`}
            autoFocus
            maxLength={200}
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            className="h-9"
          />
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-motivo`}>
          Motivo <span className="font-normal text-muted-foreground">(opcional)</span>
        </Label>
        <Textarea
          id={`${id}-motivo`}
          rows={2}
          maxLength={500}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
          placeholder="Ex.: remessa para armazém geral."
          className="min-h-0 text-xs"
        />
      </div>
      <div className="flex items-center justify-between gap-2">
        {correcao ? (
          <Button type="button" variant="ghost" size="sm" onClick={onRestaurar}>
            Restaurar calculado
          </Button>
        ) : (
          <Button type="button" variant="ghost" size="sm" onClick={onCancelar}>
            Cancelar
          </Button>
        )}
        <Button type="submit" size="sm" disabled={!podeAplicar}>
          Aplicar
        </Button>
      </div>
    </form>
  );
}

/**
 * Célula de um valor que o Tax pode corrigir. Sem `onCorrigir` e sem correção,
 * é uma célula comum; corrigida, abre o detalhe (quem, quando, por quê) mesmo
 * para quem só visualiza.
 */
export function CelulaFiscal({
  nf,
  campo,
  rotulo,
  correcao,
  onCorrigir,
  className,
}: {
  nf: NotaFiscal;
  campo: CampoSubstituivel;
  rotulo: string;
  correcao: CorrecaoTax | undefined;
  onCorrigir?: Corrigir;
  className?: string;
}) {
  const [aberto, setAberto] = React.useState(false);
  const calculado = nf[campo];

  if (!correcao && !onCorrigir) {
    return <TableCell className={cn("text-center", className)}>{calculado}</TableCell>;
  }

  return (
    <TableCell className={cn("text-center", className)}>
      <Popover open={aberto} onOpenChange={setAberto}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${onCorrigir ? "Corrigir" : "Ver correção de"} ${rotulo} da NF ${nf.n}`}
            title={correcao ? descrever(correcao, calculado) : "Clique para corrigir"}
            className={cn(
              "group/valor relative -mx-1.5 inline-flex min-h-7 items-center justify-center rounded-md px-1.5 py-0.5",
              "transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
              correcao && "bg-amber-50 ring-1 ring-amber-200 hover:bg-amber-100",
              aberto && "bg-secondary",
            )}
          >
            {correcao ? (
              <span className="flex flex-col items-center leading-tight">
                <span className="font-extrabold text-amber-700">{correcao.valor}</span>
                <span className="text-[11px] font-normal text-muted-foreground line-through">
                  {correcao.original ?? calculado}
                </span>
              </span>
            ) : (
              calculado
            )}
            {onCorrigir && !correcao && (
              <Pencil
                aria-hidden
                className="nao-imprimir absolute -right-1.5 -top-1.5 size-3 text-muted-foreground opacity-0 transition-opacity group-hover/valor:opacity-100 group-focus-visible/valor:opacity-100"
              />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="space-y-3 text-left">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
              NF {nf.n} · {rotulo}
            </p>
            <p className="mt-0.5">
              Calculado:{" "}
              <span className={cn("font-extrabold numerico", correcao && "line-through")}>
                {correcao?.original ?? calculado ?? "—"}
              </span>
            </p>
          </div>
          {correcao?.por && (
            <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-900">
              Corrigido para <strong>{correcao.valor}</strong> por {correcao.por}
              {correcao.em && ` em ${dataBR(correcao.em)}`}.
              {correcao.motivo && <span className="mt-1 block italic">“{correcao.motivo}”</span>}
            </p>
          )}
          {onCorrigir && (
            <FormCorrecao
              rotulo={rotulo}
              correcao={correcao}
              onCancelar={() => setAberto(false)}
              onRestaurar={() => {
                onCorrigir(nf.chave, campo, null);
                setAberto(false);
              }}
              onAplicar={(valor, motivo) => {
                onCorrigir(nf.chave, campo, { valor, motivo });
                setAberto(false);
              }}
            />
          )}
        </PopoverContent>
      </Popover>
    </TableCell>
  );
}

export function IndicadorGravacao({
  estado,
  onTentarDeNovo,
}: {
  estado: EstadoGravacao;
  onTentarDeNovo?: () => void;
}) {
  return (
    <span
      role="status"
      className={cn(
        "nao-imprimir inline-flex items-center gap-1 text-xs font-semibold",
        estado === "erro" ? "text-destructive" : "text-muted-foreground",
      )}
    >
      {estado === "salvando" && (
        <>
          <Loader2 className="size-3.5 animate-spin" /> Salvando…
        </>
      )}
      {estado === "salvo" && (
        <>
          <Check className="size-3.5 text-emerald-600" /> Correções salvas
        </>
      )}
      {estado === "erro" && (
        <>
          <AlertCircle className="size-3.5" /> Não foi possível salvar.
          {onTentarDeNovo && (
            <button type="button" className="underline" onClick={onTentarDeNovo}>
              Tentar de novo
            </button>
          )}
        </>
      )}
    </span>
  );
}
